import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useTranslations } from 'next-intl'

import {
  useDisclosure,
  Text,
  Stack,
  Flex,
  Box,
  ButtonGroup,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Image,
  Tooltip,
  HStack,
  VStack,
  Divider,
  Link,
  useColorModeValue,
} from '@chakra-ui/react'

import {
  updateDefaultMiningPool,
  getUserMiningPools,
  deleteMiningPoolAccount,
} from '@/api/miningPool'

import { IMiningPoolNode } from '@/types/miningPool.interface'

import EditPoolAccountModal from './EditPoolAccountModal'
import ConfirmationModal from './ConfirmationModal'

import { setMiningPoolImage } from '@/layouts/DashboardLayout/constants'

import { PencilIcon } from '@/components/icons/PencilIcon'
import { WatcherLinkIcon } from '@/components/icons/WatcherLinkIcon'
import { DeleteIcon } from '@/components/icons/DeleteIcon'
import { StarFilledIcon } from '@/components/icons/StarFilledIcon'

type MiningPoolsTableProps = {
  userMiningPools: { userMiningPools: { nodes: IMiningPoolNode[] } }
}

const MiningPoolsTable: React.FC<MiningPoolsTableProps> = ({
  userMiningPools,
}) => {
  const t = useTranslations()
  const toast = useToast()

  const [editMiningPoolItem, setEditMiningPoolItem] =
    useState<IMiningPoolNode>()
  const [defaultMiningPoolId, setDefaultMiningPoolId] = useState('')
  const [deleteMiningPoolId, setDeleteMiningPoolId] = useState('')

  const {
    isOpen: isEditPoolModalOpen,
    onOpen: onEditPoolModalOpen,
    onClose: onEditPoolModalClose,
  } = useDisclosure()

  const {
    isOpen: isConfirmationModalOpen,
    onOpen: onConfirmationModalOpen,
    onClose: onConfirmationModalClose,
  } = useDisclosure()

  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure()

  const [
    updateDefaultMiningPoolMutation,
    {
      loading: updateDefaultMiningPoolLoading,
      error: updateDefaultMiningPoolError,
    },
  ] = useMutation(updateDefaultMiningPool, {
    variables: {
      miningPoolAccountId: defaultMiningPoolId,
    },
    onCompleted: (data) => {
      onConfirmationModalClose()
      if (data?.updateDefaultMiningPool?.boolean) {
        toast({
          title: t('dashboard.mining-pool.default-pool-updated'),
          description: t('dashboard.mining-pool.default-pool-updated-desc'),
          status: 'success',
          position: 'top',
        })
      }
    },
    onError: (error) => {
      console.error('Default pool update error', error)
    },
    refetchQueries() {
      return [
        {
          query: getUserMiningPools,
        },
      ]
    },
  })

  useEffect(() => {
    if (updateDefaultMiningPoolError) {
      toast({
        title: t('dashboard.mining-pool.default-pool-updated-error'),
        description: updateDefaultMiningPoolError?.message || '',
        status: 'error',
        position: 'top',
      })
    }
  }, [updateDefaultMiningPoolError])

  const [
    deleteMiningPoolAccountMutation,
    { loading: deleteMiningPoolLoading, error: deleteMiningPoolError },
  ] = useMutation(deleteMiningPoolAccount, {
    variables: {
      miningPoolAccountId: deleteMiningPoolId,
    },
    onCompleted: (data) => {
      onDeleteConfirmationModalClose()
      if (data?.deletePoolAccount?.boolean) {
        toast({
          title: t('dashboard.mining-pool.pool-account-deleted'),
          description: t('dashboard.mining-pool.pool-account-deleted-desc'),
          status: 'success',
          position: 'top',
        })
      }
    },
    onError: (error) => {
      console.error('Pool delete error', error)
    },
    refetchQueries() {
      return [
        {
          query: getUserMiningPools,
        },
      ]
    },
  })

  useEffect(() => {
    if (deleteMiningPoolError) {
      toast({
        title: t('dashboard.mining-pool.pool-account-deleted-error'),
        description: deleteMiningPoolError?.message || '',
        status: 'error',
        position: 'top',
      })
    }
  }, [deleteMiningPoolError])

  const rowBg = useColorModeValue('white', 'whiteAlpha.50')
  const rowBorder = useColorModeValue('gray.100', 'whiteAlpha.100')
  const gray900 = useColorModeValue('gray.900', 'white.100')
  const gray200 = useColorModeValue('gray.200', 'whiteAlpha.200')
  const gray800 = useColorModeValue('gray.800', 'gray.100')

  return (
    <Stack w="full">
      {/* Mobile Mining Pool Card */}
      {userMiningPools?.userMiningPools?.nodes?.map((pool: IMiningPoolNode) => (
        <Box
          display={{
            base: 'block',
            lg: 'none',
          }}
          rounded={'md'}
          mt={4}
          p={{ base: '3', md: '6' }}
          bg={rowBg}
          border="1px solid"
          borderColor={rowBorder}
          key={pool.miningPoolAccountId}
        >
          <VStack
            alignItems="flex-start"
            justify={'space-between'}
            p={{ base: '0', md: '2' }}
          >
            <Flex direction="column" w="full">
              <Flex align={'center'} mb={1}>
                <Image
                  src={setMiningPoolImage(pool?.miningPoolName)}
                  alt={pool?.miningPoolName}
                  w={8}
                  h={8}
                  mr={3}
                />
                <Box>
                  <Text fontWeight="bold">
                    {t('dashboard.mining-pool.mining-pool')}
                  </Text>
                  <Text>{pool.miningPoolName}</Text>
                </Box>
              </Flex>

              <Text fontWeight="bold" mt={2}>
                {t('dashboard.mining-pool.pool-account')}
              </Text>
              <Text
                pb={2}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
                whiteSpace={'nowrap'}
                w={'full'}
              >
                {pool.miningPoolAccountUserName}
              </Text>
            </Flex>

            <Divider />
            <Flex pt={2} w="full" align="center" justify="space-between">
              <Box>
                {pool?.miningPoolAccountDefaultPool ? (
                  <Flex align="center">
                    <StarFilledIcon color={'brand.300'} h={4} w={4} />
                    <Text fontSize="sm" variant="brandGradient" ml={2}>
                      {t('dashboard.mining-pool.default-pool')}
                    </Text>
                  </Flex>
                ) : (
                  <Tooltip
                    label={t('dashboard.mining-pool.tooltip-make-default')}
                    hasArrow
                    arrowSize={10}
                    placement="top"
                    rounded={'md'}
                  >
                    <Button
                      variant={'outline'}
                      size={'sm'}
                      onClick={() => {
                        setDefaultMiningPoolId(pool.miningPoolAccountId)
                        onConfirmationModalOpen()
                      }}
                      _hover={{
                        color: 'brand.600',
                      }}
                    >
                      {t('dashboard.mining-pool.make-default')}
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <HStack alignItems="center" gap="1" align={'center'}>
                {pool?.miningPoolAccountWatcherUri?.length > 0 && (
                  <Tooltip
                    label={t('dashboard.mining-pool.tooltip-watcher')}
                    hasArrow
                    arrowSize={10}
                    placement="top"
                    rounded={'md'}
                  >
                    <Button
                      borderRadius={'md'}
                      border="1px solid"
                      borderColor={gray200}
                      bg="transparent"
                      p="1"
                      _hover={{
                        bg: 'gray.200',
                        color: 'brand.600',
                      }}
                      color={gray800}
                      as={Link}
                      href={pool?.miningPoolAccountWatcherUri}
                      isExternal
                    >
                      <WatcherLinkIcon />
                    </Button>
                  </Tooltip>
                )}

                <Tooltip
                  label={t('dashboard.mining-pool.tooltip-edit-pool')}
                  hasArrow
                  arrowSize={10}
                  placement="top"
                  rounded={'md'}
                >
                  <Button
                    borderRadius={'md'}
                    border="1px solid"
                    borderColor={gray200}
                    bg="transparent"
                    p="1"
                    _hover={{
                      bg: 'gray.200',
                      color: 'brand.600',
                    }}
                    onClick={() => {
                      setEditMiningPoolItem(pool)
                      onEditPoolModalOpen()
                    }}
                  >
                    <PencilIcon
                      _hover={{
                        color: 'brand.500',
                      }}
                    />
                  </Button>
                </Tooltip>

                <Tooltip
                  label={
                    pool?.miningPoolAccountDefaultPool
                      ? t('dashboard.mining-pool.tooltip-delete-pool-disabled')
                      : t('dashboard.mining-pool.tooltip-delete-pool')
                  }
                  hasArrow
                  arrowSize={10}
                  placement="top"
                  rounded={'md'}
                >
                  <Button
                    borderRadius={'md'}
                    border="1px solid"
                    borderColor={gray200}
                    bg="transparent"
                    p="1"
                    _hover={{
                      bg: 'gray.200',
                      color: 'brand.600',
                    }}
                    isDisabled={pool?.miningPoolAccountDefaultPool}
                    isLoading={
                      pool?.miningPoolAccountDefaultPool &&
                      deleteMiningPoolLoading
                    }
                    onClick={() => {
                      setDeleteMiningPoolId(pool.miningPoolAccountId)
                      onDeleteConfirmationModalOpen()
                    }}
                  >
                    <DeleteIcon w={5} h={5} />
                  </Button>
                </Tooltip>
              </HStack>
            </Flex>
          </VStack>
        </Box>
      ))}

      {/* Mining Pool Table  */}
      <TableContainer
        display={{
          base: 'none',
          lg: 'block',
        }}
      >
        <Table
          variant="unstyled"
          bg={'none'}
          style={{
            borderTop: '15px solid transparent',
            borderCollapse: 'separate',
            borderSpacing: '0 8px',
          }}
        >
          <Thead>
            <Tr>
              <Th
                fontSize={'sm'}
                textTransform={'capitalize'}
                fontWeight={600}
                color={gray900}
                pb={1}
              >
                {t('dashboard.mining-pool.mining-pool')}
              </Th>
              <Th
                fontSize={'sm'}
                textTransform={'capitalize'}
                fontWeight={600}
                color={gray900}
                pb={1}
              >
                {t('dashboard.mining-pool.pool-account')}
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {userMiningPools?.userMiningPools?.nodes?.map(
              (pool: IMiningPoolNode) => (
                <Tr
                  key={pool.miningPoolAccountId}
                  bg={rowBg}
                  sx={{
                    '& td:first-of-type': {
                      borderLeftRadius: '8px',
                    },
                    '& td:last-of-type': {
                      borderRightRadius: '8px',
                    },
                  }}
                >
                  <Td
                    fontSize={'sm'}
                    w="25%"
                    border="1px solid"
                    borderRight="none"
                    borderColor={rowBorder}
                  >
                    <Flex alignItems="center" gap="1">
                      <Image
                        src={setMiningPoolImage(pool?.miningPoolName)}
                        alt={pool?.miningPoolName}
                        w={9}
                        h={9}
                        mr={2}
                      />
                      {pool.miningPoolName}
                    </Flex>
                  </Td>
                  <Td
                    fontSize={'sm'}
                    pb={2}
                    textOverflow={'ellipsis'}
                    overflow={'hidden'}
                    whiteSpace={'nowrap'}
                    w={{ base: '24', md: '52' }}
                    border="1px solid"
                    borderLeft="none"
                    borderRight="none"
                    borderColor={rowBorder}
                  >
                    {pool.miningPoolAccountUserName}
                  </Td>
                  <Td
                    border="1px solid"
                    borderLeft="none"
                    borderColor={rowBorder}
                  >
                    <Flex justifyContent={'flex-end'} align={'center'}>
                      <ButtonGroup gap="2">
                        <Flex
                          alignItems="center"
                          gap="1"
                          justifyContent={'center'}
                          align={'center'}
                          margin={'auto'}
                        >
                          {pool?.miningPoolAccountDefaultPool ? (
                            <>
                              <StarFilledIcon color={'brand.300'} h={4} w={4} />
                              <Text fontSize="sm" variant="brandGradient">
                                {t('dashboard.mining-pool.default-pool')}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Tooltip
                                label={t(
                                  'dashboard.mining-pool.tooltip-make-default'
                                )}
                                hasArrow
                                arrowSize={10}
                                placement="top"
                                rounded={'md'}
                              >
                                <Button
                                  variant={'outline'}
                                  size={'sm'}
                                  onClick={() => {
                                    setDefaultMiningPoolId(
                                      pool.miningPoolAccountId
                                    )
                                    onConfirmationModalOpen()
                                  }}
                                  _hover={{
                                    color: 'brand.600',
                                  }}
                                >
                                  {t('dashboard.mining-pool.make-default')}
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Flex>
                        {pool?.miningPoolAccountWatcherUri?.length > 0 && (
                          <Tooltip
                            label={t('dashboard.mining-pool.tooltip-watcher')}
                            hasArrow
                            arrowSize={10}
                            placement="top"
                            rounded={'md'}
                          >
                            <Button
                              borderRadius={'md'}
                              border="1px solid"
                              borderColor={gray200}
                              bg="transparent"
                              p="1"
                              color={gray800}
                              as={Link}
                              isExternal
                              href={pool?.miningPoolAccountWatcherUri}
                              _hover={{
                                bg: 'gray.200',
                                color: 'brand.600',
                              }}
                            >
                              <WatcherLinkIcon />
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip
                          label={t('dashboard.mining-pool.tooltip-edit-pool')}
                          hasArrow
                          arrowSize={10}
                          placement="top"
                          rounded={'md'}
                        >
                          <Button
                            borderRadius={'md'}
                            border="1px solid"
                            borderColor={gray200}
                            bg="transparent"
                            p="1"
                            _hover={{
                              bg: 'gray.200',
                              color: 'brand.600',
                            }}
                            onClick={() => {
                              onEditPoolModalOpen()
                              setEditMiningPoolItem(pool)
                            }}
                          >
                            <PencilIcon />
                          </Button>
                        </Tooltip>

                        <Tooltip
                          label={
                            pool?.miningPoolAccountDefaultPool
                              ? t(
                                  'dashboard.mining-pool.tooltip-delete-pool-disabled'
                                )
                              : t('dashboard.mining-pool.tooltip-delete-pool')
                          }
                          hasArrow
                          arrowSize={10}
                          placement="top"
                          rounded={'md'}
                        >
                          <Button
                            borderRadius={'md'}
                            border="1px solid"
                            borderColor={gray200}
                            bg="transparent"
                            p="1"
                            _hover={{
                              bg: 'gray.100',
                              color: 'brand.600',
                            }}
                            isDisabled={pool?.miningPoolAccountDefaultPool}
                            isLoading={
                              pool?.miningPoolAccountDefaultPool &&
                              deleteMiningPoolLoading
                            }
                            onClick={() => {
                              setDeleteMiningPoolId(pool.miningPoolAccountId)
                              onDeleteConfirmationModalOpen()
                            }}
                          >
                            <DeleteIcon
                              color={
                                pool?.miningPoolAccountDefaultPool
                                  ? 'gray.400'
                                  : ''
                              }
                              w={5}
                              h={5}
                            />
                          </Button>
                        </Tooltip>
                      </ButtonGroup>
                    </Flex>
                  </Td>
                </Tr>
              )
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <EditPoolAccountModal
        isOpen={isEditPoolModalOpen}
        onClose={onEditPoolModalClose}
        userMiningPoolItem={editMiningPoolItem}
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={onConfirmationModalClose}
        confirmationFunction={updateDefaultMiningPoolMutation}
        title={t('dashboard.mining-pool.confirmation-modal-default')}
        description={t(
          'dashboard.mining-pool.confirmation-modal-default-description'
        )}
        loading={updateDefaultMiningPoolLoading}
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        onClose={onDeleteConfirmationModalClose}
        confirmationFunction={deleteMiningPoolAccountMutation}
        title={t('dashboard.mining-pool.confirmation-modal-delete')}
        description={t(
          'dashboard.mining-pool.confirmation-modal-delete-description'
        )}
        loading={deleteMiningPoolLoading}
      />
    </Stack>
  )
}

export default MiningPoolsTable
